import { Injectable, inject } from "@angular/core";
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";

import { Week } from "./week";

@Injectable({
    providedIn: "root"
})
export class DatepickerService {
    private readonly calendar = inject(NgbCalendar);
    private readonly dateParserFormatter = inject(NgbDateParserFormatter);

    parseTextDate(value: string, onlyMonths = false): NgbDate | undefined {
        if (onlyMonths && value.length === 7) {
            value += "-01";
        }

        const parsed = this.dateParserFormatter.parse(value);
        if (!parsed) {
            return undefined;
        }
        return new NgbDate(parsed.year, parsed.month, parsed.day);
    }

    parseMinDate(value: any, onlyMonths = false): NgbDate {
        const minDate = this.parseDate(value);
        if (!minDate) {
            return undefined;
        }
        const day = onlyMonths ? 1 : minDate.day;
        return new NgbDate(minDate.year, minDate.month, day);
    }

    parseMaxDate(value: any, onlyMonths = false): NgbDate {
        const maxDate = this.parseDate(value);
        if (!maxDate) {
            return undefined;
        }
        const day = onlyMonths ? 1 : maxDate.day;
        return new NgbDate(maxDate.year, maxDate.month, day);
    }

    parseDate(value: any): NgbDateStruct {
        let result: NgbDateStruct;
        if (value instanceof Date) {
            result = {
                day: value.getUTCDate(),
                month: value.getUTCMonth() + 1,
                year: value.getUTCFullYear()
            };
        } else if (typeof value === "object") {
            result = value;
        } else {
            result = this.dateParserFormatter.parse(value);
        }
        return result;
    }

    toDisplayText(date: NgbDate, onlyMonths = false) {
        const formatted = this.toTextFormatter(date);
        if (onlyMonths && formatted) {
            return formatted.substring(0, 7);
        }
        return formatted;
    }

    toTextFormatter = (i: NgbDateStruct) => i ? this.dateParserFormatter.format(i) : undefined;

    getWeekDates(date: NgbDate) {
        const monday = this.getMonday(date);
        const dates = [monday];
        for (let i = 1; i < 7; i++) {
            dates.push(this.calendar.getNext(monday, "d", i));
        }
        return dates;
    }

    getStartDate(sourceDate: NgbDate) {
        return this.getMonday(new NgbDate(sourceDate.year, sourceDate.month, 1));
    }

    getMonday(date: NgbDate) {
        const weekDay = this.calendar.getWeekday(date);
        const diff = weekDay - 1;
        if (diff > 0) {
            return this.calendar.getPrev(date, "d", diff);
        }
        return date;
    }

    getMonthWeeks(startDate: NgbDate): Week[] {
        const result = [];
        const count = this.calendar.getWeeksPerMonth();
        let currentWeekDate = startDate;
        for (let i = 0; i < count; i++) {
            const weekDates = this.getWeekDates(currentWeekDate);
            const weekNumber = this.calendar.getWeekNumber(weekDates, 1);
            result.push({ week: weekNumber,
                dates: weekDates });
            currentWeekDate = this.calendar.getNext(currentWeekDate, "d", 7);
        }
        return result;
    }

    getMonths(viewDate: NgbDate) {
        const result = [];
        const names = this.getMonthNames();
        for (let i = 0; i < names.length; i++) {
            result.push({
                name: names[i],
                // eslint-disable-next-line sonarjs/deprecation
                nameShort: names[i].substr(0, 3),
                // eslint-disable-next-line id-blacklist
                number: i,
                startDate: new NgbDate(viewDate.year, i + 1, 1)
            });
        }
        return result;
    }

    getWeekDayNames() {
        return moment.weekdays(true);
    }

    getMonthNames() {
        return moment.months();
    }

}
